// src/pages/WeeklyPlanPage.js

import React, { useMemo, useRef, useState, useEffect } from "react";
import BackButton from "../components/BackButton";
import { CaretLeft, CaretRight, Plus } from "phosphor-react";

const getMonday = (d0 = new Date()) => {
  const d = new Date(d0.getFullYear(), d0.getMonth(), d0.getDate());
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const formatRangeDate = (date) => {
  const d = new Date(date);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

export default function WeeklyPlanPage() {
  const [offsetWeeks, setOffsetWeeks] = useState(0);

  const baseMonday = useMemo(() => getMonday(new Date()), []);

  const viewMonday = useMemo(
    () => addDays(baseMonday, offsetWeeks * 7),
    [baseMonday, offsetWeeks]
  );

  const storageKey = useMemo(
    () => `tp:weekplan:page:${ymd(viewMonday)}`,
    [viewMonday]
  );

  const days = useMemo(
    () => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    []
  );

  const dayDateLabels = useMemo(
    () =>
      Object.fromEntries(
        days.map((d, i) => {
          const date = addDays(viewMonday, i);
          return [d, `${date.getMonth() + 1}/${date.getDate()}`];
        })
      ),
    [days, viewMonday]
  );

  const placeholderMap = {
    Mon: "6AM Solidcore",
    Tue: "7:30PM Guitar lesson",
    Wed: "6AM Solidcore",
    Thu: "8PM Pottery",
    Fri: "6AM Solidcore",
    Sat: "2PM Reading",
    Sun: "6:30PM Cooking class @ Eataly",
  };

  const emptyDay = () => ["", ""];

  const emptyPlan = useMemo(
    () => Object.fromEntries(days.map((d) => [d, emptyDay()])),
    [days]
  );

  const [plan, setPlan] = useState(emptyPlan);

  const focusRef = useRef(null);
  const inputRefs = useRef({});

  const setInputRef = (day, idx) => (el) => {
    if (!inputRefs.current[day]) inputRefs.current[day] = {};
    inputRefs.current[day][idx] = el;
  };

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);

      if (!raw) {
        setPlan(emptyPlan);
        return;
      }

      const parsed = JSON.parse(raw);
      const next = {};

      for (const d of days) {
        const v = parsed?.[d];

        if (Array.isArray(v)) next[d] = v.length ? v : emptyDay();
        else if (typeof v === "string") next[d] = v ? [v] : emptyDay();
        else next[d] = emptyDay();
      }

      setPlan(next);
    } catch {
      setPlan(emptyPlan);
    }
  }, [storageKey, emptyPlan, days]);

  useEffect(() => {
    const f = focusRef.current;
    if (!f) return;

    const el = inputRefs.current?.[f.day]?.[f.idx];

    if (el) {
      el.focus();

      try {
        el.setSelectionRange(f.caret, f.caret);
      } catch {}

      autoResize(el);
    }
  }, [plan]);

  const save = () => {
    const cleaned = {};

    for (const d of days) {
      const list = (plan[d] || []).map((s) => (s ?? "").trim());
      while (list.length && !list[list.length - 1]) list.pop();
      cleaned[d] = list;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(cleaned));
    } catch {}
  };

  const setDayItems = (day, updater) =>
    setPlan((p) => {
      const base = Array.isArray(p[day]) ? [...p[day]] : emptyDay();
      const next = typeof updater === "function" ? updater(base) : updater;
      return { ...p, [day]: next };
    });

  const DayCard = ({ day }) => {
    const items = plan[day] || emptyDay();

    const addLine = () => setDayItems(day, (arr) => [...arr, ""]);

    const onChangeItem = (idx, e) => {
      const value = e.target.value;

      focusRef.current = {
        day,
        idx,
        caret: e.target.selectionStart ?? value.length,
      };

      setDayItems(day, (arr) => {
        const next = [...arr];
        next[idx] = value;

        if ((next[next.length - 1] ?? "").trim()) next.push("");

        return next;
      });

      autoResize(e.target);
    };

    const onKeyDown = (idx, e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        focusRef.current = { day, idx: idx + 1, caret: 0 };
        addLine();
      }
    };

    return (
      <div
        className="
          group rounded-[22px] border border-blue-100/80
          bg-white/90 shadow-[0_4px_0px_rgba(37,99,235,0.96)]
          min-h-[178px] overflow-hidden px-1
        "
      >
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-blue-700 text-sm instrument-font font-bold uppercase">
              {day}
            </div>

            <div className="rounded-full bg-blue-50 instrument-font px-2 py-0.5 text-sm font-bold text-blue-400">
              {dayDateLabels[day]}
            </div>
          </div>

          <div className="h-[3px] w-8 rounded-full bg-blue-400/70" />
        </div>

        <div className="ml-1.5 px-2.5 pb-2 space-y-1.5">
          {items.map((val, i) => (
            <div key={i} className="relative">
              <div className="pointer-events-none absolute left-2 top-[13px] h-1.5 w-1.5 rounded-full bg-blue-300/80" />

              <textarea
                ref={(el) => {
                  setInputRef(day, i)(el);
                  if (el) autoResize(el);
                }}
                value={val}
                onChange={(e) => onChangeItem(i, e)}
                onKeyDown={(e) => onKeyDown(i, e)}
                placeholder={i === 0 ? placeholderMap[day] : "Add plan"}
                rows={1}
                className="
                  w-full resize-none overflow-hidden
                  rounded-xl border border-transparent bg-blue-50/45
                  pl-5 pr-2 py-1.5 text-sm leading-[18px] text-blue-950
                  outline-none transition
                  focus:border-blue-200 focus:bg-white
                  whitespace-pre-wrap break-words
                  placeholder:text-blue-300
                "
              />
            </div>
          ))}
        </div>

        <div className="px-3 pb-4">
          <button
            type="button"
            onClick={addLine}
            className="
              !box-border !inline-flex !min-h-0 !h-7
              !items-center !gap-1 !rounded-full
              !border !border-blue-100 !bg-white !px-3
              !text-xs !leading-none !font-bold !text-blue-500
              !transition hover:!bg-blue-50 appearance-none
            "
          >
            <Plus size={12} weight="bold" className="shrink-0" />
            Add
          </button>
        </div>
      </div>
    );
  };

  const weekStart = formatRangeDate(viewMonday);
  const weekEnd = formatRangeDate(addDays(viewMonday, 6));

  return (
    <div className="min-h-screen bg-blue-50">
      <header
        className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mt-2 mx-auto px-4 flex items-centerr">
          <BackButton className="text-blue-600" ariaLabel="Back" />

          <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setOffsetWeeks((n) => n - 1)}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Previous week"
              title="Previous week"
            >
              <CaretLeft size={23} weight="fill" />
            </button>

            <h1 className="text-[20px] text-blue-800 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none whitespace-nowrap">
              Weekly Plan
            </h1>

            <button
              type="button"
              onClick={() => setOffsetWeeks((n) => n + 1)}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Next week"
              title="Next week"
            >
              <CaretRight size={23} weight="fill" />
            </button>
          </div>
        </div>

        <div className="-mt-2 text-center text-sm font-semibold text-blue-500">
          {weekStart} – {weekEnd}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pt-3">
        <div className="md:hidden space-y-2.5">
          {days.map((d) => (
            <DayCard key={`m-${d}`} day={d} />
          ))}
        </div>

        <div className="hidden md:grid md:grid-cols-7 md:gap-2.5">
          {days.map((d) => (
            <DayCard key={`d-${d}`} day={d} />
          ))}
        </div>

        <div className="mt-5 flex justify-end">
          <button
            onClick={save}
            className="
              !px-3 !min-h-0 !min-w-0 !py-1 rounded-full bg-blue-600
              text-white text-sm font-medium
            "
          >
            Save
          </button>
        </div>
      </main>

      <div style={{ height: "4rem" }} />
    </div>
  );
}